// Roster-as-attendance table: name · department · track · assignment badge ·
// attendance select · duration · note. Edits stay local until Save, which
// emits ONLY the rows that changed (PUT /attendance {marks}). "Mark all
// present" sets every row to attended. Read-only for viewers who may look
// but not mark (champions) — the server enforces the same rule.
import { useT } from '../lib/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, UserCheck } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import type { Attendance, AttendanceMark, AttendanceStatus } from '@/services/learningService';
import { AssignmentTypeBadge } from './AssignmentTypeBadge';
import { ATTENDANCE_STATUSES, AttendanceChip, attendanceLabel } from './AttendanceChip';

interface Draft { status: AttendanceStatus; duration: string; note: string }
const selectCls = 'h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground disabled:opacity-50';

const toDraft = (r: Attendance): Draft => ({ status: r.status || 'invited', duration: r.duration_minutes != null ? String(r.duration_minutes) : '', note: r.note || '' });
const sameDraft = (a: Draft, b: Draft) => a.status === b.status && a.duration === b.duration && a.note === b.note;

interface Props {
  rows: Attendance[];
  readOnly?: boolean;
  saving?: boolean;
  onSave: (marks: AttendanceMark[]) => void;
  className?: string;
}

/** Diff the working copy against the loaded rows → PUT body (changed rows only). */
export function diffMarks(rows: Attendance[], drafts: Record<string, Draft>): AttendanceMark[] {
  return rows.flatMap((r) => {
    const d = drafts[r.user_id];
    if (!d || sameDraft(d, toDraft(r))) return [];
    const mark: AttendanceMark = { user_id: r.user_id, status: d.status };
    const mins = d.duration.trim() === '' ? undefined : Number(d.duration);
    if (mins !== undefined && Number.isFinite(mins) && mins >= 0) mark.duration_minutes = Math.round(mins);
    if (d.note.trim() || r.note) mark.note = d.note.trim();
    return [mark];
  });
}

export const AttendanceGrid: React.FC<Props> = ({ rows, readOnly, saving, onSave, className }) => {
  const t = useT();
  const base = useMemo(() => Object.fromEntries(rows.map((r) => [r.user_id, toDraft(r)])), [rows]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>(base);
  // Re-seed when the server rows change (after Save / another marker).
  useEffect(() => { setDrafts(base); }, [base]);
  const patch = (uid: string, p: Partial<Draft>) => setDrafts((m) => ({ ...m, [uid]: { ...(m[uid] || base[uid]), ...p } }));
  const changed = useMemo(() => diffMarks(rows, drafts), [rows, drafts]);
  const markAll = () => setDrafts((m) => Object.fromEntries(rows.map((r) => [r.user_id, { ...(m[r.user_id] || base[r.user_id]), status: 'attended' as AttendanceStatus }])));

  return (
    <div className={`space-y-2 ${className || ''}`} data-testid="attendance-grid">
      {!readOnly && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button size="sm" variant="outline" disabled={rows.length === 0 || saving} onClick={markAll} data-testid="mark-all-present">
            <UserCheck className="mr-1 h-4 w-4" aria-hidden />{t('training:att_mark_all', 'Mark all present')}
          </Button>
          <div className="flex items-center gap-2">
            {changed.length > 0 && <span className="text-xs text-muted-foreground">{t('training:att_changed_n', '{{n}} change(s)', { n: changed.length })}</span>}
            <Button size="sm" disabled={changed.length === 0 || saving} onClick={() => onSave(changed)} data-testid="save-attendance">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:save', 'Save')}
            </Button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground text-xs">
            <tr>
              <th className="text-left font-medium px-2.5 py-2">{t('training:col_name', 'Name')}</th>
              <th className="text-left font-medium px-2.5 py-2">{t('training:wiz_department', 'Department')}</th>
              <th className="text-left font-medium px-2.5 py-2">{t('training:track', 'Track')}</th>
              <th className="text-left font-medium px-2.5 py-2">{t('training:att_col_attendance', 'Attendance')}</th>
              <th className="text-left font-medium px-2.5 py-2">{t('training:att_col_minutes', 'Minutes')}</th>
              <th className="text-left font-medium px-2.5 py-2">{t('training:att_col_note', 'Note')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => {
              const d = drafts[r.user_id] || base[r.user_id];
              return (
                <tr key={r.user_id} data-testid={`att-row-${r.user_id}`}>
                  <td className="px-2.5 py-1.5">
                    <div className="text-foreground">{r.name}</div>
                    <div className="mt-0.5"><AssignmentTypeBadge type={r.assignment_type} /></div>
                  </td>
                  <td className="px-2.5 py-1.5 text-muted-foreground">{r.department || '—'}</td>
                  <td className="px-2.5 py-1.5 text-muted-foreground">{r.track_name || '—'}</td>
                  <td className="px-2.5 py-1.5">
                    {readOnly ? <AttendanceChip status={r.status} /> : (
                      <select aria-label={t('training:att_status_for', 'Attendance for {{name}}', { name: r.name })} className={selectCls} value={d.status} disabled={saving}
                        onChange={(e) => patch(r.user_id, { status: e.target.value as AttendanceStatus })}>
                        {ATTENDANCE_STATUSES.map((s) => <option key={s} value={s}>{attendanceLabel(t, s)}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-2.5 py-1.5">
                    {readOnly ? <span className="text-muted-foreground tabular-nums">{r.duration_minutes ?? '—'}</span> : (
                      <Input aria-label={t('training:att_minutes_for', 'Minutes for {{name}}', { name: r.name })} className="h-8 w-20" type="number" min={0} value={d.duration} disabled={saving}
                        onChange={(e) => patch(r.user_id, { duration: e.target.value })} />
                    )}
                  </td>
                  <td className="px-2.5 py-1.5">
                    {readOnly ? <span className="text-muted-foreground">{r.note || ''}</span> : (
                      <Input aria-label={t('training:att_note_for', 'Note for {{name}}', { name: r.name })} className="h-8 min-w-[8rem]" value={d.note} disabled={saving}
                        onChange={(e) => patch(r.user_id, { note: e.target.value })} />
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={6} className="px-2.5 py-4 text-center text-muted-foreground">{t('training:no_participants', 'No participants yet.')}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceGrid;
